import {useMutation, useQuery} from "@tanstack/react-query";
import Card from "../../common/Card";
import {Content, ContentFixed, ContentScroll} from "../../common/Content";
import {HeaderMenu} from "../../common/HeaderMenu";
import clsx from "clsx";
import styles from "./Contacts.module.scss";
import {FabButton} from "../../common/FabButton";
import React, { useEffect, useState } from "react";
import ContactEditor from "./ContactEditor";
import {MODE_CREATE, MODE_EDIT} from "../../codes";
import {apiDeleteContact, apiGetContacts} from "../../api/ApiContacts";
import Form from "react-bootstrap/esm/Form";
import {useSearchParams} from "react-router";
import {useDebounce} from "use-debounce";
import {ItemActions} from "../../common/ItemActions";
import {DeleteModal} from "../../common/AppModal";
import useToast from "../../util/useToast";
import {AppPagination} from "../../common/AppPagination";

const Contacts = () => {
  const {showToastSuccess} = useToast();

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInputValue, setSearchInputValue] = useState(searchParams.get("filter") || "");
  const [debouncedFilter] = useDebounce(searchInputValue, 200);

  const [contactEditor, setContactEditor] = useState({show: false, contact: {}});
  const [deleteContactModal, setDeleteContactModal] = useState({show: false, contact: null});

  useEffect(() => {
    const filter = debouncedFilter.trim();

    if (filter !== (searchParams.get("filter") || "")) {
      setSearchParams(filter ? { filter } : {});
    }
  }, [debouncedFilter]);

  const queryParams = {size: 50, page: +searchParams.get("page") || 0};

  if (searchParams.get("filter")) {
    queryParams["filter"] = searchParams.get("filter");
  }

  let contactsQuery = useQuery({
    ...apiGetContacts(queryParams),
    enabled: false,
  });

  const deleteMutation = useMutation(apiDeleteContact());

  const contacts = contactsQuery.data.content;
  const totalPages = contactsQuery.data.page.totalPages;
  const totalElements = contactsQuery.data.page.totalElements;

  return <Content>
    <ContentFixed>
      <HeaderMenu title="Tous les contacts"/>
    </ContentFixed>

    <ContentScroll paddingTop withFab>
      <div className={styles.searchBar}>
        <Form.Control
          type="text"
          className={styles.searchInput}
          placeholder="Chercher"
          value={searchInputValue}
          onChange={e => setSearchInputValue(e.target.value)}
        />
      </div>

      <Card>
        <div className={clsx(styles.contact, styles.header)}>
          <div className={styles.view}>
            <div className={styles.contactName}>Nom du contact</div>
            <div className={styles.contactAddress}>Adresses</div>
            <div className={styles.contactPhone}>Téléphones</div>
            <div className={styles.contactEmail}>Emails</div>
            <div className={styles.contactDetails}>Détails</div>
          </div>
        </div>

        {contacts.length === 0 && <div className={styles.empty}>Pas de résultats</div>}

        {contacts.map(contact => {
          const contactColor = contact.color.split("|");

          const contactActions = [
            {
              type: "item",
              icon: "edit",
              label: "Modifier",
              onClick: () => setContactEditor({show: true, mode: MODE_EDIT, contact})
            },
            {type: "divider"},
            {
              type: "item",
              icon: "delete",
              label: "Supprimer",
              onClick: () => setDeleteContactModal({show: true, contact})
            },
          ];

          return <div key={contact.id} className={styles.contact} style={{borderColor: contactColor[0]}}>
            <div className={styles.view}>
              <div className={styles.contactName}>{contact.name}</div>
              <div className={styles.contactInfos}>
                <div className={styles.contactAddresses}>
                  {contact.addresses.length === 0 ? <div className={styles.contactEmpty}>-</div> : null}

                  {contact.addresses
                    .sort((a, b) => a.address.localeCompare(b.address))
                    .map(address => {
                    return <div key={address.id}>{address.address}{address.label && " (" + address.label + ")"}</div>
                  })}
                </div>
                <div className={styles.contactPhones}>
                  {contact.phones.length === 0 ? <div className={styles.contactEmpty}>-</div> : null}

                  {contact.phones
                    .sort((a, b) => a.phone.localeCompare(b.phone))
                    .map(phone => {
                    return <div key={phone.id}>{phone.phone}{phone.label && " (" + phone.label + ")"}</div>
                  })}
                </div>
                <div className={styles.contactEmails}>
                  {contact.emails.length === 0 ? <div className={styles.contactEmpty}>-</div> : null}

                  {contact.emails
                    .sort((a, b) => a.email.localeCompare(b.email))
                    .map(email => {
                    return <div key={email.id}>{email.email}{email.label && " (" + email.label + ")"}</div>
                  })}
                </div>
              </div>
            </div>

            <ItemActions actions={contactActions}/>
          </div>;
        })}
      </Card>

      <Card padding>
        <AppPagination
          itemName="contact"
          page={queryParams.page}
          totalElements={totalElements}
          totalPages={totalPages}
        />
      </Card>
    </ContentScroll>

    <FabButton onClick={() => setContactEditor({show: true, mode: MODE_CREATE, contact: {}})}/>

    <ContactEditor
      show={contactEditor.show}
      hide={() => setContactEditor(prev => ({...prev, show: false}))}
      mode={contactEditor.mode}
      onSubmit={() => contactsQuery.refetch()}
      contact={contactEditor.contact}
    />

    <DeleteModal
      show={deleteContactModal.show}
      title="Supprimer le contact"
      onCancel={() => setDeleteContactModal({show: false})}
      onSubmit={() => {
        return deleteMutation.mutateAsync(deleteContactModal.contact.id)
          .then(() => contactsQuery.refetch())
          .then(() => {
            showToastSuccess("Contact supprimé");

            setDeleteContactModal({show: false});
          });
      }}
      cancelLabel="Annuler"
      submitLabel="Supprimer"
    >
      Etes-vous sûr de vouloir supprimer ce contact ?
    </DeleteModal>
  </Content>;
};

export default Contacts;